import {
  EmptyState,
  FormCard,
  FormItem,
  Input,
  NoDataIcon,
  Select,
} from '@lmc/ui-kit'
import { DeleteButton } from 'screens/Rostering/components'
import { Button, Text } from '@lmc/ui-kit/src/general'
import {
  getFilteredFundingTypeOptions,
  getRequiredRule,
} from 'screens/Rostering/utils'
import { useTranslation } from 'react-i18next'
import { Form } from 'antd'
import { DefaultOptionType } from 'antd/es/select'
import { InputSuffix } from 'screens/Rostering/formComponents'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

type FundingFormItemProps = {
  onDelete: () => void
  name: number
  fundingTypeOptions: DefaultOptionType[]
  selectedResident: string | undefined
}

export const FundingFormItem = ({
  onDelete,
  name,
  fundingTypeOptions,
  selectedResident,
}: FundingFormItemProps) => {
  const { t } = useTranslation()

  const getFundingSourceTitle = (id: string) => {
    const fundingOption = fundingTypeOptions.find(
      (fundingOption) => fundingOption.value === id
    )

    return fundingOption?.label
  }

  const fundingUrl = selectedResident
    ? `/residents/${selectedResident}/care-package-and-patterns`
    : '/residents'

  return (
    <Form.Item
      shouldUpdate={(prevState, currentState) =>
        prevState?.fundingSources[name]?.type !==
        currentState?.fundingSources[name]?.type
      }
    >
      {({ getFieldValue }) => {
        return (
          <FormCard
            level={2}
            title={
              getFundingSourceTitle(
                getFieldValue(['fundingSources', name, 'type'])
              ) || `Funding ${name + 1}`
            }
            rightChildren={
              <DeleteButton onClick={onDelete}>
                {t('DELETE_FUNDING')}
              </DeleteButton>
            }
          >
            <FormItem
              name={[name, 'type']}
              label={<Text>{t('FUNDING_TYPE')}</Text>}
              rules={[getRequiredRule(t('FUNDING_TYPE'))]}
              data-cy="funding-type-dropdown"
            >
              <Select
                data-cy="dropdown-select-funding-type"
                options={getFilteredFundingTypeOptions(
                  fundingTypeOptions,
                  getFieldValue('fundingSources')
                )}
                notFoundContent={
                  <EmptyState
                    title={
                      <EmptyStateWrapper>
                        <Text>{t('SELECT_EMPTY_STATE_FUNDING_1')}</Text>
                        <Text>{t('SELECT_EMPTY_STATE_FUNDING_2')}</Text>
                      </EmptyStateWrapper>
                    }
                    description={
                      <Link to={fundingUrl} target="_blank" rel="noreferrer">
                        <Button variant="primary">{t('EDIT')}</Button>
                      </Link>
                    }
                    icon={
                      <NoDataIconWrapper>
                        <NoDataIcon />
                      </NoDataIconWrapper>
                    }
                  />
                }
              />
            </FormItem>
            <FormItem
              name={[name, 'hoursPerWeek']}
              label={<Text>{t('HOURS_FUNDED')}</Text>}
              rules={[getRequiredRule(t('HOURS_FUNDED'))]}
            >
              <Input
                type="number"
                suffix={<InputSuffix>{t('HOURS_PER_WEEK')}</InputSuffix>}
                min={0}
                data-cy="funded-hours-input"
              />
            </FormItem>
          </FormCard>
        )
      }}
    </Form.Item>
  )
}

const EmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const NoDataIconWrapper = styled.div`
  scale: 0.5;
  height: 50px;
  display: flex;
  align-items: center;
`
